import React, { useState, useEffect} from "react";
import { render } from "react-dom";
import ReactMapGL, {
  _MapContext as MapContext,
  ScaleControl,
  NavigationControl, Popup
} from "react-map-gl";
import DeckGL from "@deck.gl/react";
import { TripsLayer } from "@deck.gl/geo-layers";
import mapboxgl from "mapbox-gl";
import {Typography, Tooltip} from "@material-ui/core/";
import { withStyles } from "@material-ui/styles";

//CSS
import "./Map1.css";

//components
import Map1Control from "./Map1Control";
import Legend from "./Legend.js";
import LegendResistance from "../Map3Components/LegendResistance";
import LegendAgLand from "./LegendAgLands";
import LegendPVR from "./LegendPVR";
import LegendCounty from "./LegendCounty";
import LegendCounty2 from "./LegendCounty2";
import Logo from "../Logo";
import MapboxLogo from "../mapboxLogo";
import { MAPBOX_TOKEN } from "../../config";

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const mapboxToken = MAPBOX_TOKEN

const DEFAULT_THEME = {
  trailColor0: [255, 255, 255],
};

const INITIAL_VIEW_STATE = {
  longitude: -102.879576,
  latitude: 39.1283,
  zoom: 3.95,
  pitch: 0,
  bearing: 0,
};

const scaleControlStyle = {
  right: 10,
  bottom: 108,
  borderRadius: "5px",
};

export default function App({
  trailLength = 380,
  initialViewState = INITIAL_VIEW_STATE,
  theme = DEFAULT_THEME,
  loopLength = 2800, // unit corresponds to the timestamp in source data
  animationSpeed = 1.2,
  // animationSpeed = 4.6
}) {
  const [time, setTime] = useState(0);
  const [animation] = useState({});
  const [mapStyle, setMapStyle] = useState("");
  const [activeLayer, setActiveLayer] = useState("Connectivity");
  const [hoverData, setHoverData] = useState('');
  const [renderPopup, setRenderPopup] = useState(false)
  const [deckCheck, setDeckCheck] = useState(false);
  const [viewport, setViewport] = useState({
    latitude: 38.3283,
    longitude: -98.579576,
    zoom: 5.5,
    width: "100vw",
    height: "92.65vh",
  });

  const onHover = (event) => {
    // console.log("event", event)
    const features = App.queryRenderedFeatures([event.x, event.y], {
      layers: ["County-level Values"]
    })
    // console.log("features", features);
    if (Object.keys(features).length !== 0) {
    setHoverData({
      longitude: event.coordinate[0],
      latitude: event.coordinate[1],
      countyName: features[0] && features[0].properties.NAME,
      stateName: features[0] && features[0].properties.StateName,
      agAcres: features[0] && features[0].properties.agAcres,
      percentAg: features[0] && features[0].properties.percentAg,
      conRank: features[0] && features[0].properties.con_rank,
      pvrRank: features[0] && features[0].properties.pvr_rank,
    })
    setRenderPopup(true);
  } else {
    setRenderPopup(false);
  }
  };

  const animate = () => {
    setTime((t) => (t + animationSpeed) % loopLength);
    animation.id = window.requestAnimationFrame(animate);
  };

  useEffect(() => {
    animation.id = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(animation.id);
  }, [animation, activeLayer]);

  const layers = [
    deckCheck
      ? ["1", "2", "3", "4", "5", "6"].map((idnum) => {
          return new TripsLayer({
            id: "3000_" + idnum,
            data:
              // "https://storage.googleapis.com/connectivity-app/current-paths-v018/current-path-conus-3000-"
              "https://storage.googleapis.com/connectivity-app/current-paths/current-path-conus-3000-"
              +
              idnum +
              ".json",
            getPath: (d) => d.coords,
            getTimestamps: (d) => d.timestamp,
            getColor: theme.trailColor0,
            opacity: 0.8,
            widthMinPixels: 3,
            rounded: true,
            trailLength,
            currentTime: time,
            visible: true,
            shadowEnabled: false,
            pickable: true,
          });
        })
      : null,
  ];

  return (
    <>
      <Map1Control
        onChange={setMapStyle}
        activeLayer={activeLayer}
        setActiveLayer={setActiveLayer}
      />

      <DeckGL
        ContextProvider={MapContext.Provider}
        layers={layers}
        effects={theme.effects}
        initialViewState={initialViewState}
        controller={true}
        onHover={onHover}
      >
        <div
          className="navControl"
          style={{ position: "absolute", right: 40, top: 75, zIndex: 1 }}
        >
          <NavigationControl className={"navControl"} showCompass={false} />
        </div>

        <ReactMapGL
          {...viewport}
          // onViewportChange={(viewport) => setViewport(viewport)}
          mapboxApiAccessToken={mapboxToken}
          mapStyle={mapStyle}
          interactiveLayerIds={['County-level Values']}
          ref={ref => { App = ref && ref.getMap(); }}
        >
            <ScaleControl
              maxWidth={100}
              unit="metric"
              style={scaleControlStyle}
              className="scaleControl"
            />
        </ReactMapGL>

        <div className="deck-control">
          {/* <p className="title">Important Movement Pathways</p> */}
          <input
            type="checkbox"
            id={"deckLayers"}
            onChange={(e) => setDeckCheck(e.target.checked)}
            checked={deckCheck}
          />
          <Tooltip placement="right" title={<><p style={{fontSize: "12px"}}> Simulated movement routes of organisms along high-connectivity pathways, illustrating the potential for landscapes across the U.S. to support long-distance movements.</p> </>} arrow>
              <Typography className="label" htmlFor={"decklayers"}>
                Important Movement Pathways
              </Typography>
          </Tooltip>
        </div>

        <div className="flex-container">
          <div className="map1Legend1">
            {activeLayer === "Connectivity" && <Legend />}
            {activeLayer === "Landscape Resistance" && <LegendResistance />}
            {activeLayer === "Agricultural Land Cover" && <LegendAgLand />}
            {activeLayer === "PVR" && <LegendPVR />}
            {activeLayer === "Conservation Opportunity" && <LegendCounty />}
            {activeLayer === "County-level Values" && <LegendCounty2 />}

          </div>
        </div>

        {renderPopup === true && (
        <div style={{zIndex: 1000000}}>
          <Popup
            longitude={hoverData.longitude}
            latitude={hoverData.latitude}
            style={{backgroundColor: "white"}}
            closeOnClick={false}
            closeButton={false}
            onClose={() => setRenderPopup(false)}
            >
              <body><b>{hoverData.countyName}, {hoverData.stateName}</b></body>
              {/* <body>Agricultural Acres: {hoverData.agAcres}</body> */}
              <body>Percent of County in Ag: {hoverData.percentAg}</body>
              <body>Agricultural (PVR): {hoverData.pvrRank}</body>
              <body>Connectivity: {hoverData.conRank}</body>
          </Popup>
          </div>
        )}  
      </DeckGL>
      <MapboxLogo />
      <Logo className="fullScreenLogo" height={70} width={175} />
    </>
  );
}

export function renderToDOM(container) {
  render(<App />, container);
}
